// services/poolaReport.service.js
//
// Data access for the supplier "final report" (post-mortem) — a per-deal,
// per-supplier performance snapshot for a CLOSED Poola deal (brainstorm
// _bmad-output/planning-artifacts/poola-supplier-deal-report-brainstorm-2026-07-15.md).
//
// The BE aggregate is DELIVERED — read the real contract, don't guess:
//   ../backend/_bmad-output/sync/briefs/2026-07-15-poola-supplier-deal-report-aggregate-BE-REPLY.md
//   ../backend/app/Http/Controllers/Api/V1/Poola/PoolaDealController.php (supplierReport())
//   ../backend/app/Services/Commerce/PoolaSupplierDealReportService.php
//
// Uses `jsonApiClient` ONLY so the Sanctum SPA cookie + X-XSRF-TOKEN + the
// shared 401/419 interceptors flow through, and so the JSON:API deserializer
// runs. NEVER introduce a new top-level axios instance — see the comment block
// in src/httpConfig/config/axios.js (multiple Laravel session cookies →
// "400 Request Header Too Large").
//
// Two reads power the report:
//   1. fetchSupplierReport(dealId) — the aggregate (people + poolcredit + the
//      named leaderboard). `data: null` document; the payload is meta-only
//      (`meta.supplierReport`), same shape as /poola-deals/current's ineligible
//      branch. 403 = not a supplier · 404 = deal not closed / wrong club.
//   2. fetchMyOffersForDeal(dealId) — the viewer's own offers in the deal, for
//      the client-derived money + sell-through half. A SCOPED read (not
//      usePoolaOffers) so a report load never flushes the shared PoolaOffer
//      pinia-orm repo out from under a live buy surface.
import { POOLA_OFFERS_PAGE_LIMIT } from '@/constants/poola.js';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { readPageMeta } from '@/utils/jsonApiCursor.js';

// Stable typed-error codes the composable branches on (mirrors the
// supplierBoxInvoice.service.js posture: a normalized Error carrying .code +
// .status so the caller never re-sniffs response.status).
export const POOLA_REPORT_FORBIDDEN = 'poola_report_forbidden'; // 403 → viewer is not a supplier
export const POOLA_REPORT_NOT_AVAILABLE = 'poola_report_not_available'; // 404 → deal not closed / wrong club / unknown

const OFFERS_ENDPOINT = '/poola-offers';

// A hard page-count ceiling so a pathological cursor loop (a present-but-
// unparseable links.next that never advances) can't spin forever. A single Poola
// deal's offer count is far below 100 × this cap; if it ever isn't, the report
// under-counts loudly (via the console.warn) rather than hanging.
const MAX_OFFER_PAGES = 20;

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

function normalizeError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) normalized.code = POOLA_REPORT_FORBIDDEN;
  else if (status === 404) normalized.code = POOLA_REPORT_NOT_AVAILABLE;

  return normalized;
}

/**
 * Fetch the supplier deal-report aggregate for a CLOSED deal.
 *
 * GET /poola-deals/{dealId}/supplier-report → HTTP 200 with `data: null`; the
 * payload is `meta.supplierReport`. Integer cents throughout, string ids.
 *
 * @param {string|number} dealId — a specific ENDED deal id (NOT `current`).
 * @returns {Promise<object>} `meta.supplierReport` (see the BE reply for the
 *   full shape: `{ dealId, distinctMemberCount, viewer:{...}, leaderboard:[...] }`).
 * @throws {Error} normalized with `.code` (POOLA_REPORT_FORBIDDEN on 403,
 *   POOLA_REPORT_NOT_AVAILABLE on 404) + `.status` on any HTTP failure.
 */
export async function fetchSupplierReport(dealId) {
  try {
    const response = await jsonApiClient.get(`/poola-deals/${dealId}/supplier-report`);
    // jsonApiClient deserializes to `{ data, meta, links }` on `response.data`
    // (axios.js transformJsonApiResponse). `data` is null here — read from meta.
    return response?.data?.meta?.supplierReport ?? null;
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Fetch ALL of the viewer's own (`isMine`) offers in a deal, paging the cursor
 * to completion. Mirrors usePoolaOffers.buildQueryParams exactly — ONLY
 * `filter[deal]` + `page[limit]` (+ `page[after]`); NEVER sort/include/sparse
 * fields (each 422s; a sparse read also nulls `canClaim`). Full (non-sparse)
 * read ⇒ `isMine` is authoritative.
 *
 * @param {string|number} dealId — the deal (= filter[deal]).
 * @returns {Promise<object[]>} the deserialized `isMine === true` offers
 *   (camelCase attributes flattened onto each object; money in integer cents).
 * @throws {Error} normalized on HTTP failure (no 403/404 special-casing — the
 *   report aggregate is the gate; this read is the supporting money/units half).
 */
export async function fetchMyOffersForDeal(dealId) {
  const mine = [];
  const seen = new Set();
  let cursor = null;

  try {
    for (let page = 0; page < MAX_OFFER_PAGES; page += 1) {
      const params = {
        'filter[deal]': dealId,
        'page[limit]': POOLA_OFFERS_PAGE_LIMIT,
      };
      if (cursor) params['page[after]'] = cursor;

      const response = await jsonApiClient.get(OFFERS_ENDPOINT, { params });
      const doc = response?.data ?? {};
      const rows = doc.data ?? [];

      for (const row of rows) {
        // De-dupe across overlapping cursors, then keep only the viewer's own.
        if (row?.id == null || seen.has(row.id)) continue;
        seen.add(row.id);
        if (row.isMine === true) mine.push(row);
      }

      const { cursor: next, hasMore } = readPageMeta(doc);
      if (!hasMore || !next) return mine;
      cursor = next;
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[poolaReport.service] offer paging hit MAX_OFFER_PAGES (${MAX_OFFER_PAGES}) for deal ${dealId}; report may under-count.`,
    );
    return mine;
  } catch (error) {
    throw normalizeError(error);
  }
}
